import { useTranslation } from "react-i18next";
import { PackageStepLayout } from "../PackageStepLayout";
import { Stack, Text } from "@certn/cascadia";
import { Container } from "@certn/cascadia-react";
import { AdjudicationBetaBadge } from "../../../../../components/adjudication-beta/AdjudicationBetaBadge";
import { useContext, useState, useEffect } from "react";
import { SelectExistingRubricSection } from "./components/SelectExistingRubricSection";
import {
  RubricSelectionRadioGroup,
  RubricSelectionValue,
} from "../../../../../components/adjudication-rubric-selection/RubricSelectionRadioGroup";
import { Formik } from "formik";
import { object, string } from "yup";
import { PackageContext } from "../../PackageContext";
import { useClientPortalAPI } from "@certn/api";

export function AdjudicationStep() {
  const { t } = useTranslation();
  const [rubricSelection, setRubricSelection] =
    useState<RubricSelectionValue>("USE_DEFAULT");

  const { goToNextStep } = useContext(PackageContext);
  const { packageData, setPackage } = useContext(PackageContext);

  useEffect(() => {
    if (packageData.rubric_id) {
      setRubricSelection("SELECT_EXISTING");
    } else {
      setRubricSelection("USE_DEFAULT");
    }
  }, [packageData]);

  const response = useClientPortalAPI(
    "/api/internal/client-portal/adjudication-rubrics/filter-by-check-type/",
    {
      // Sometimes the check_type is not the key
      // if an explicit check_type is selected, then use the value of the selected option
      check_type: packageData.checks.map((check) => {
        const selectedOption =
          Array.isArray(check.selectedOptions) &&
          check.selectedOptions.length > 0
            ? check.selectedOptions[0]
            : undefined;
        return selectedOption &&
          selectedOption.select_field_argument === "explicit_check_type"
          ? selectedOption.value
          : check.key;
      }),
    }
  );

  const formSchema = object().shape({
    rubric_id: string().nullable(),
  });

  return (
    <Formik
      initialValues={{
        rubric_id: packageData.rubric_id || null,
      }}
      validationSchema={formSchema}
      onSubmit={(values) => {
        const updatedPackage = {
          ...packageData,
          rubric_id:
            rubricSelection === "SELECT_EXISTING" ? values.rubric_id : null,
        };
        setPackage(updatedPackage);
        goToNextStep();
      }}
    >
      {({ handleSubmit, isSubmitting, values }) => {
        return (
          <PackageStepLayout
            onComplete={() => {
              handleSubmit();
            }}
            submitting={isSubmitting}
          >
            <Container layout="narrow">
              <Stack gap="space-2" orientation="horizontal">
                <Text as="h1" style="global/title-small">
                  {t("Optional scoring rules")}
                </Text>
                <AdjudicationBetaBadge />
              </Stack>
              <Text style="form/body-primary">
                {t(
                  "Scoring rules determine how package results are evaluated. You can use default scoring or apply a rubric (a saved set of scoring rules) to customise outcomes."
                )}
              </Text>
              <RubricSelectionRadioGroup
                value={rubricSelection}
                onChange={setRubricSelection}
              />
              {rubricSelection === "SELECT_EXISTING" && (
                <SelectExistingRubricSection
                  response={response}
                  rubric_id={values.rubric_id}
                />
              )}
            </Container>
          </PackageStepLayout>
        );
      }}
    </Formik>
  );
}
